import { Fragment, type ReactNode } from 'react'
import type { JSONContent } from '@/types/api'
import type { SnapshotIndices } from './snapshotIndices'
import { formatCitationText } from '@/components/workbench/CitationNode'

/**
 * Render a frozen TipTap doc to React as the read-only share view.
 *
 * Deliberately *not* mounting a real TipTap editor here — the share view
 * doesn't need ProseMirror's runtime, just the visual output. Walking
 * the JSON tree gives full control over rendering and keeps the public
 * bundle slim.
 *
 * Citation markers use the same ``citation-sup`` class + ASCII range
 * notation (``1-3``, ``1,4-6``) as the authenticated editor (see
 * ``components/workbench/CitationNode.ts``), so the visual matches the
 * in-app view byte for byte and copy-pasting yields readable text.
 *
 * Supported block types match what the editor actually emits today
 * (paragraph, heading 1-3, bulletList, orderedList, listItem,
 * blockquote, hardBreak). Marks: bold, italic, underline. Unknown
 * nodes/marks are passed through as their text content so weird
 * inputs don't blank the view.
 */

interface ShareDocumentProps {
  doc: JSONContent
  indices: SnapshotIndices
  /** Click handler for citation markers. When provided, the share-view
   *  recipient can click a citation to jump into the matching claim's
   *  Surgery panel — mirrors the in-app interaction. ``claimId`` is the
   *  citation node's ``claimId`` attr (= the claim row's DB UUID). */
  onCitationClick?: (claimId: string) => void
}

interface RenderCtx {
  cursor: { citationIndex: number }
  indices: SnapshotIndices
  onCitationClick?: (claimId: string) => void
}

function renderMarks(text: string, marks: Array<{ type: string }> | undefined, keyPrefix: string): ReactNode {
  if (!marks || marks.length === 0) return text
  let out: ReactNode = text
  let depth = 0
  for (const mark of marks) {
    const key = `${keyPrefix}-mark-${depth++}`
    switch (mark.type) {
      case 'bold': out = <strong key={key}>{out}</strong>; break
      case 'italic': out = <em key={key}>{out}</em>; break
      case 'underline': out = <u key={key}>{out}</u>; break
      default: break // unknown mark — render plain
    }
  }
  return out
}

function renderInline(node: JSONContent, key: string, ctx: RenderCtx): ReactNode {
  const obj = node as Record<string, unknown>
  if (obj.type === 'text') {
    const text = typeof obj.text === 'string' ? obj.text : ''
    return renderMarks(text, obj.marks as Array<{ type: string }> | undefined, key)
  }
  if (obj.type === 'hardBreak') return <br key={key} />
  if (obj.type === 'citation') {
    const occ = ctx.indices.perCitation[ctx.cursor.citationIndex++]
    if (!occ || occ.indices.length === 0) {
      // Every source on this citation was hidden in review_state — drop
      // the marker entirely so the visible text reads cleanly.
      return null
    }
    const clickable = !!ctx.onCitationClick && !!occ.claimId
    return (
      <sup
        key={key}
        className="citation-sup"
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? () => ctx.onCitationClick!(occ.claimId) : undefined}
        onKeyDown={clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                ctx.onCitationClick!(occ.claimId)
              }
            }
          : undefined}
      >
        {formatCitationText(occ.indices)}
      </sup>
    )
  }
  return null
}

function renderInlineList(children: JSONContent[] | undefined, keyPrefix: string, ctx: RenderCtx): ReactNode[] {
  if (!children) return []
  return children.map((c, i) => (
    <Fragment key={`${keyPrefix}-${i}`}>{renderInline(c, `${keyPrefix}-${i}`, ctx)}</Fragment>
  ))
}

function renderBlock(node: JSONContent, key: string, ctx: RenderCtx): ReactNode {
  const obj = node as Record<string, unknown>
  const children = obj.content as JSONContent[] | undefined
  switch (obj.type) {
    case 'paragraph':
      return (
        <p key={key} className="my-3 leading-7 text-[15px] text-foreground">
          {renderInlineList(children, key, ctx)}
        </p>
      )
    case 'heading': {
      const level = ((obj.attrs as Record<string, unknown> | undefined)?.level as number) ?? 1
      const inline = renderInlineList(children, key, ctx)
      if (level === 1) return <h1 key={key} className="mt-6 mb-3 text-2xl font-semibold text-foreground">{inline}</h1>
      if (level === 2) return <h2 key={key} className="mt-5 mb-2 text-xl font-semibold text-foreground">{inline}</h2>
      return <h3 key={key} className="mt-4 mb-2 text-lg font-semibold text-foreground">{inline}</h3>
    }
    case 'bulletList':
      return (
        <ul key={key} className="my-3 list-disc pl-6 space-y-1 text-[15px] text-foreground">
          {(children ?? []).map((c, i) => renderBlock(c, `${key}-${i}`, ctx))}
        </ul>
      )
    case 'orderedList':
      return (
        <ol key={key} className="my-3 list-decimal pl-6 space-y-1 text-[15px] text-foreground">
          {(children ?? []).map((c, i) => renderBlock(c, `${key}-${i}`, ctx))}
        </ol>
      )
    case 'listItem':
      // listItem children are typically paragraphs; render them inline-ish.
      return (
        <li key={key} className="leading-7">
          {(children ?? []).map((c, i) => renderBlock(c, `${key}-${i}`, ctx))}
        </li>
      )
    case 'blockquote':
      return (
        <blockquote key={key} className="my-4 border-l-2 border-border pl-4 italic text-muted-foreground">
          {(children ?? []).map((c, i) => renderBlock(c, `${key}-${i}`, ctx))}
        </blockquote>
      )
    case 'horizontalRule':
      return <hr key={key} className="my-6 border-border" />
    default:
      // Unknown block — render whatever children it has so we don't
      // silently drop text.
      if (children) {
        return <div key={key}>{children.map((c, i) => renderBlock(c, `${key}-${i}`, ctx))}</div>
      }
      return null
  }
}

export function ShareDocument({ doc, indices, onCitationClick }: ShareDocumentProps) {
  const ctx: RenderCtx = { cursor: { citationIndex: 0 }, indices, onCitationClick }
  const top = doc as Record<string, unknown>
  const children = top.content as JSONContent[] | undefined
  if (!children) return null
  return <div>{children.map((c, i) => renderBlock(c, `b-${i}`, ctx))}</div>
}
